<template>
    <div class="portal-page">
        <!-- 顶部品牌区：携程 logo + 文字 -->
        <div class="nav-bar">
            <div class="nav-brand">
                <img class="nav-logo" src="/static/logo.png" alt="携程logo" />
                <span class="nav-title">携程</span>
            </div>
            <span class="nav-subtitle">心理健康关爱服务 · 专业 · 安全 · 保密</span>
        </div>

        <!-- Hero 双栏区：左 banner + 右双卡 -->
        <div class="hero-zone">
            <!-- 左侧大 banner（白底文字区 + 底部插画，对应参考UI飞盘运动卡） -->
            <div class="hero-banner" @click="goToChat">
                <div class="banner-text">
                    <div class="banner-title">AI心理陪伴</div>
                    <div class="banner-desc">智能对话式心理疏导</div>
                    <div class="banner-go">
                        <span class="banner-go-text">查看详情</span>
                        <span class="banner-go-btn">GO</span>
                    </div>
                </div>
                <div class="banner-pic">
                    <img
                        class="banner-img"
                        src="/static/images/portal-banner.png"
                        alt="AI心理陪伴"
                    />
                </div>
            </div>

            <!-- 右侧卡位 -->
            <div class="hero-side">
                <!-- AI无感心理筛查：上图下文（与左 banner 的上文下图镜像对称） -->
                <div class="venue-card" @click="goToAssessment">
                    <div class="venue-pic">
                        <img
                            class="venue-img"
                            src="/static/images/portal-venue.png"
                            alt="AI无感心理筛查"
                        />
                    </div>
                    <div class="venue-head">
                        <div class="venue-text">
                            <div class="venue-title">AI无感心理筛查</div>
                            <div class="venue-subtitle">多维度情绪状态评估</div>
                        </div>
                    </div>
                    <div class="venue-go">
                        <span class="venue-go-text">去检测</span>
                        <span class="venue-go-btn">GO</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- 专业量表区：静态量表列表，点击跳转 iframe 测评页 -->
        <div class="scale-section">
            <div class="section-bar">
                <div class="section-indicator"></div>
                <span class="section-heading">专业量表(部分)</span>
            </div>

            <div
                class="scale-card"
                v-for="item in evaluations"
                :key="item.evalID"
                @click="openEvaluation(item)"
            >
                <div class="scale-info">
                    <div class="scale-title-row">
                        <span class="scale-title">{{ item.title }}</span>
                        <span class="scale-tag">{{ item.tag }}</span>
                    </div>
                    <div class="scale-desc">{{ item.desc }}</div>
                </div>
                <div class="scale-arrow">›</div>
            </div>
        </div>

        <!-- 底部声明 -->
        <div class="footer-bar">
            <span class="footer-line"></span>
        </div>

        <!-- 登录弹窗 -->
        <div class="modal-mask" v-if="showLoginModal" @click="showLoginModal = false">
            <div class="login-modal" @click.stop>
                <div class="login-modal-header">
                    <span class="login-modal-title">身份验证</span>
                    <span class="login-modal-close" @click="showLoginModal = false">×</span>
                </div>
                <div class="login-modal-body">
                    <div class="login-input-wrapper">
                        <span class="login-input-prefix">+86</span>
                        <input
                            class="login-phone-input"
                            type="number"
                            v-model="loginPhone"
                            placeholder="请输入手机号"
                            maxlength="11"
                        />
                    </div>
                    <div
                        class="login-submit-btn"
                        :class="{ disabled: !canLogin || isLogining }"
                        @click="handleLogin"
                    >
                        <span>{{ isLogining ? '验证中...' : '登录' }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { request } from '@/utils/request.js';
import { getStorage, setStorage } from '@/utils/storage.js';

export default {
    data() {
        return {
            showLoginModal: false, // 是否显示登录弹窗
            loginPhone: '', // 登录输入的手机号
            isLogining: false, // 是否正在登录
            pendingAction: null, // 登录成功后要执行的动作（'chat' | 'assessment'）
            // 专业量表静态列表（中科新测评系统，点击跳 iframe 测评页）
            evaluations: [
                {
                    title: '抑郁情绪自评量表',
                    tag: 'SDS',
                    desc: '20题 · 约5分钟',
                    scale: 'SDS',
                },
                {
                    title: '青少年心理素养联合量表',
                    tag: 'SPM-74',
                    desc: '74题 · 约15分钟',
                    scale: 'SPM-74',
                },
                {
                    title: '症状自评量表',
                    tag: 'SCL90',
                    desc: '90题 · 约20分钟',
                    scale: 'SCL90',
                },
            ],
        };
    },
    computed: {
        // 手机号格式是否合法
        canLogin() {
            return /^1[3-9]\d{9}$/.test(this.loginPhone.trim());
        },
    },
    methods: {
        /**
         * 检查是否已登录（token 有效）
         * @returns {Promise<boolean>} 是否已登录
         */
        async isLoggedIn() {
            const token = getStorage('token');
            if (!token) return false;
            try {
                const res = await request({ url: '/api/verify-token', method: 'GET' });
                if (res.data.code === 200) {
                    const userData = res.data.data;
                    setStorage('userPhone', userData.phoneNumber);
                    setStorage('userId', userData.userId);
                    setStorage('hasCompletedGuide', userData.hasCompletedGuide);
                    setStorage('hasCompletedAssessment', userData.hasCompletedAssessment);
                    return true;
                }
            } catch (e) {
                // token 验证失败
            }
            return false;
        },

        /** 跳转到聊天首页（先检查登录状态） */
        async goToChat() {
            if (await this.isLoggedIn()) {
                this.doGoToChat();
            } else {
                this.pendingAction = 'chat';
                this.loginPhone = '';
                this.showLoginModal = true;
            }
        },

        /** 打开心理筛查网页 */
        goToAssessment() {
            window.location.href = 'https://emotion.deruimu.net';
        },

        /** 打开量表测评页（iframe 嵌入中科新测评系统） */
        openEvaluation(item) {
            this.$router.push(`/evaluation?scale=${item.scale}`);
        },

        /** 登录弹窗中点击登录 */
        async handleLogin() {
            if (!this.canLogin || this.isLogining) return;
            this.isLogining = true;
            try {
                const res = await request({
                    url: '/api/phone-login',
                    method: 'POST',
                    data: { phoneNumber: this.loginPhone.trim() },
                    needAuth: false,
                });
                if (res.data.code === 200) {
                    const userData = res.data.data;
                    setStorage('token', userData.token);
                    setStorage('userPhone', userData.phoneNumber);
                    setStorage('userId', userData.userId);
                    setStorage('hasCompletedGuide', userData.hasCompletedGuide);
                    setStorage('hasCompletedAssessment', userData.hasCompletedAssessment);
                    this.showLoginModal = false;
                    if (this.pendingAction === 'chat') {
                        this.doGoToChat();
                    }
                    this.pendingAction = null;
                } else {
                    this.$toast(res.data.message || '登录失败');
                }
            } catch (e) {
                this.$toast('网络错误，请重试');
            } finally {
                this.isLogining = false;
            }
        },

        /** 执行跳转聊天首页（未完成引导则先进引导页） */
        doGoToChat() {
            if (getStorage('hasCompletedGuide')) {
                this.$router.push('/chat');
            } else {
                this.$router.push('/guide');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
/* ==================== 页面基础 ==================== */
.portal-page {
    width: 100vw;
    min-height: 100vh;
    // 携程蓝：顶部浅蓝渐变过渡到浅灰
    background: linear-gradient(180deg, #d8ebff 0%, #e9f4ff 24%, #f5f7fa 60%);
    font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue', sans-serif;
}

/* ==================== 顶部品牌区 ==================== */
.nav-bar {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 36rpx 0 8rpx;
}

// logo + 品牌名横排
.nav-brand {
    display: flex;
    align-items: center;
}

.nav-logo {
    width: 64rpx;
    height: 64rpx;
    margin-right: 12rpx; // 与文字间距（替代 gap）
    display: block;
    object-fit: contain; // 保持 logo 比例不变形
}

.nav-title {
    font-size: 40rpx;
    font-weight: 700;
    color: #0a6cf5; // 携程品牌蓝
    letter-spacing: 2rpx;
}

.nav-subtitle {
    margin-top: 12rpx;
    font-size: 22rpx;
    color: #7ba4d6;
    letter-spacing: 4rpx; // 副标题字间距，营造标语感
}

/* ==================== Hero 双栏区 ==================== */
.hero-zone {
    display: flex;
    align-items: stretch;
    margin: 24rpx 32rpx 0;
}

// 左右两栏间距（替代 gap，兼容性处理）
.hero-side {
    flex: 1;
    margin-left: 20rpx;
    display: flex;
    flex-direction: column;
}

/* 左侧大 banner：白底文字区 + 底部插画（对应参考UI飞盘运动卡） */
.hero-banner {
    position: relative;
    width: 46%;
    flex-shrink: 0;
    border-radius: 24rpx;
    overflow: hidden;
    background: #ffffff;
    box-shadow: 0 8rpx 24rpx rgba(10, 108, 245, 0.15);
    display: flex;
    flex-direction: column;

    &:active {
        transform: scale(0.98);
    }
}

.banner-text {
    padding: 28rpx 24rpx 20rpx;
}

.banner-title {
    font-size: 40rpx;
    font-weight: 700;
    color: #123c7a;
}

.banner-desc {
    margin-top: 10rpx;
    font-size: 22rpx;
    color: #8a94a6;
}

// 「查看详情 GO」组合胶囊（左灰右蓝）
.banner-go {
    margin-top: 20rpx;
    display: flex;
    align-items: center;
}

.banner-go-text {
    font-size: 22rpx;
    color: #6b7688;
    background: #f2f4f8;
    border-radius: 24rpx 0 0 24rpx;
    padding: 8rpx 20rpx;
}

.banner-go-btn {
    font-size: 22rpx;
    font-weight: 700;
    color: #ffffff;
    background: linear-gradient(135deg, #5db1ff 0%, #0a6cf5 100%);
    border-radius: 0 24rpx 24rpx 0;
    padding: 8rpx 18rpx;
}

// 底部插画区（撑满剩余高度）
.banner-pic {
    flex: 1;
    position: relative;
    min-height: 240rpx;
}

.banner-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover; // 覆盖整个插画区域
    display: block;
}

/* 右侧卡：AI无感心理筛查（标题行+图片） */
.venue-card {
    flex: 1; // 独占右侧高度，与左 banner 对齐
    background: #ffffff;
    border-radius: 24rpx;
    overflow: hidden;
    box-shadow: 0 4rpx 16rpx rgba(10, 108, 245, 0.1);
    display: flex;
    flex-direction: column;

    &:active {
        transform: scale(0.98);
    }
}

.venue-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16rpx 24rpx 20rpx;
}

.venue-text {
    flex: 1;
    min-width: 0; // 右侧胶囊挤压时标题可省略
}

.venue-title {
    font-size: 30rpx;
    font-weight: 600;
    color: #123c7a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

// 副标题：浅灰蓝小字
.venue-subtitle {
    margin-top: 6rpx;
    font-size: 20rpx;
    color: #8a9ab8;
}

// 「去检测 GO」组合胶囊：独占一行（与左 banner 的查看详情 GO 同款风格）
.venue-go {
    display: flex;
    align-items: center;
    margin: 16rpx 24rpx 20rpx;
}

.venue-go-text {
    font-size: 20rpx;
    color: #6b7688;
    background: #f2f4f8;
    border-radius: 20rpx 0 0 20rpx;
    padding: 6rpx 14rpx;
}

.venue-go-btn {
    font-size: 20rpx;
    font-weight: 700;
    color: #ffffff;
    background: linear-gradient(135deg, #5db1ff 0%, #0a6cf5 100%);
    border-radius: 0 20rpx 20rpx 0;
    padding: 6rpx 14rpx;
}

.venue-pic {
    position: relative;
    flex: 1; // 图片区撑满卡片剩余高度
    margin: 20rpx 20rpx 0;
    border-radius: 16rpx;
    overflow: hidden;
}

.venue-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

/* ==================== 专业量表区（整体UI风格：独立渐变浅色条目卡） ==================== */
.scale-section {
    margin: 32rpx 32rpx 40rpx;
}

.section-bar {
    display: flex;
    align-items: center;
    padding: 0 8rpx 20rpx;
}

.section-indicator {
    width: 6rpx;
    height: 28rpx;
    border-radius: 3rpx;
    background: #0a6cf5;
    margin-right: 16rpx;
}

.section-heading {
    font-size: 30rpx;
    font-weight: 700;
    color: #123c7a;
    letter-spacing: 2rpx;
}

// 量表条目卡：白→浅蓝渐变 + 蓝描边
.scale-card {
    background: linear-gradient(135deg, #ffffff 0%, #eaf3ff 100%);
    border: 1rpx solid rgba(10, 108, 245, 0.15);
    border-radius: 20rpx;
    padding: 24rpx;
    display: flex;
    align-items: center;
    box-shadow: 0 4rpx 16rpx rgba(10, 108, 245, 0.08);

    & + .scale-card {
        margin-top: 20rpx; // 条目间距（替代 gap）
    }

    &:active {
        transform: scale(0.98);
    }
}

.scale-info {
    flex: 1;
    min-width: 0; // 允许长标题省略
}

.scale-title-row {
    display: flex;
    align-items: center;
}

.scale-title {
    font-size: 30rpx;
    font-weight: 600;
    color: #1a2332;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.scale-tag {
    flex-shrink: 0;
    font-size: 20rpx;
    color: #1173e0;
    background: rgba(10, 108, 245, 0.08);
    border-radius: 8rpx;
    padding: 4rpx 14rpx;
    margin-left: 12rpx; // 与标题间距（替代 gap）
}

.scale-desc {
    margin-top: 10rpx;
    font-size: 22rpx;
    color: #8a94a6;
}

// 右侧圆形箭头（携程蓝圆底）
.scale-arrow {
    flex-shrink: 0;
    width: 56rpx;
    height: 56rpx;
    border-radius: 50%;
    background: linear-gradient(135deg, #5db1ff 0%, #0a6cf5 100%);
    color: #ffffff;
    font-size: 32rpx;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 16rpx;
}

/* ==================== 底部 ==================== */
.footer-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20rpx 0;
}

.footer-line {
    display: inline-block;
    width: 40rpx;
    height: 1rpx;
    background: #d0d6e0;
    margin-right: 16rpx;
}

.footer-label {
    font-size: 20rpx;
    color: #b0b8c8;
    letter-spacing: 2rpx;
}

/* ==================== 登录弹窗 ==================== */
.modal-mask {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 200;
}

.login-modal {
    width: 80%;
    max-width: 560rpx;
    background: #fff;
    border-radius: 20rpx;
    overflow: hidden;
    animation: modalIn 0.25s ease;
}

@keyframes modalIn {
    from {
        opacity: 0;
        transform: scale(0.95) translateY(20rpx);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

.login-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 36rpx 36rpx 0;
}

.login-modal-title {
    font-size: 34rpx;
    font-weight: 600;
    color: #1a2332;
}

.login-modal-close {
    width: 52rpx;
    height: 52rpx;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32rpx;
    color: #8a94a6;
    border-radius: 50%;
    background: #f5f6fa;
}

.login-modal-body {
    padding: 32rpx 36rpx 40rpx;
}

.login-input-wrapper {
    display: flex;
    align-items: center;
    height: 92rpx;
    background: #f5f6fa;
    border-radius: 14rpx;
    border: 2rpx solid #ebeef5;
    margin-bottom: 28rpx;
    overflow: hidden;
}

.login-input-prefix {
    padding: 0 20rpx 0 24rpx;
    font-size: 28rpx;
    color: #8a94a6;
    border-right: 2rpx solid #ebeef5;
    margin-right: 12rpx;
    line-height: 92rpx;
}

.login-phone-input {
    flex: 1;
    height: 92rpx;
    font-size: 30rpx;
    color: #1a2332;
    padding-right: 24rpx;
    border: none;
    outline: none;
    background: transparent;
}

.login-submit-btn {
    width: 100%;
    height: 88rpx;
    // 携程蓝渐变主按钮
    background: linear-gradient(135deg, #5db1ff 0%, #0a6cf5 100%);
    border-radius: 14rpx;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8rpx 24rpx rgba(10, 108, 245, 0.3);
    transition: opacity 0.2s ease;

    span {
        font-size: 30rpx;
        color: #ffffff;
        font-weight: 600;
    }

    &.disabled {
        opacity: 0.5;
    }

    &:active {
        transform: scale(0.98);
    }
}
</style>
